import { Button } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  type ShopBranchOption,
  type SupplierListRow,
  shopInventoryApi,
  shopSuppliersApi,
} from "../api/client";

const PO_LIST_ROUTE = "/app/shops/$shopId/purchase-orders" as const;

export type ShopPurchaseOrdersListSearch = {
  offset: number;
  limit: number;
  status?: string;
  branchId?: string;
  supplierId?: string;
  due?: "soon" | "overdue";
  sortBy?:
    | "createdAt"
    | "poNumber"
    | "status"
    | "currency"
    | "branchName"
    | "lineCount";
  sortOrder?: "asc" | "desc";
};

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "__all__", label: "ທຸກສະຖານະ" },
  { value: "draft", label: "ຮ່າງ" },
  { value: "submitted", label: "ສົ່ງແລ້ວ" },
  { value: "partially_received", label: "ຮັບບາງສ່ວນ" },
  { value: "received", label: "ຮັບຄົບ" },
  { value: "canceled", label: "ຍົກເລີກ" },
];

const DUE_OPTIONS: { value: string; label: string }[] = [
  { value: "__all__", label: "ທຸກກຳນົດສົ່ງ" },
  { value: "soon", label: "ໃກ້ຄວບຄິດ (7 ວັນ)" },
  { value: "overdue", label: "ເກີນກຳນົດສົ່ງ" },
];

export function ShopPurchaseOrdersFilter() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: PO_LIST_ROUTE });
  const search = (useSearch({
    from: PO_LIST_ROUTE,
  }) ?? {}) as ShopPurchaseOrdersListSearch;

  const statusValue = search.status ?? "__all__";
  const branchIdValue = search.branchId?.trim() ?? "";
  const supplierIdValue = search.supplierId?.trim() ?? "";
  const dueSelect =
    search.due === "soon" || search.due === "overdue" ? search.due : "__all__";

  const hasFilter =
    (search.status != null && search.status !== "") ||
    (search.branchId != null && search.branchId.trim() !== "") ||
    (search.supplierId != null && search.supplierId.trim() !== "") ||
    (search.due != null && search.due !== "");

  const go = (next: Partial<ShopPurchaseOrdersListSearch>) => {
    if (!shopId) return;
    nav({
      to: PO_LIST_ROUTE,
      params: { shopId },
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        ...search,
        ...next,
      },
    });
  };

  if (!shopId) return null;

  return (
    <div className="mb-2 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <div className="flex w-full flex-col gap-1 sm:w-44">
            <span className="sr-only">ສະຖານະ</span>
            <SimpleSelect
              value={statusValue}
              onValueChange={(v) => {
                go({
                  status: v === "__all__" ? undefined : v,
                });
              }}
              options={STATUS_OPTIONS}
              placeholder="ສະຖານະ"
              className="h-8 w-full"
            />
          </div>
          <InfiniteCombobox<ShopBranchOption>
            queryKey={["shops", shopId, "purchase-orders", "branch-lookup"]}
            queryFn={async ({ search: q, pageParam }) => {
              return shopInventoryApi.lookupBranches(shopId, {
                page: pageParam,
                search: q.trim() || undefined,
                limit: 20,
              });
            }}
            preloadQueryFn={(id) => shopInventoryApi.getBranch(shopId, id)}
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={branchIdValue}
            onValueChange={(val) => {
              go({
                branchId: val.trim() !== "" ? val : undefined,
              });
            }}
            placeholder="ສາຂາ"
            className="h-8 w-full sm:w-52"
            clearable
          />
          <InfiniteCombobox<SupplierListRow>
            queryKey={["shops", shopId, "purchase-orders", "supplier-lookup"]}
            queryFn={async ({ search: q, pageParam }) => {
              const res = await shopSuppliersApi.list(shopId, {
                limit: 20,
                offset: (pageParam - 1) * 20,
                search: q.trim() || undefined,
                activeOnly: true,
              });
              const hasMore =
                res.meta.offset + res.data.length < res.meta.total;
              return {
                items: res.data,
                nextPage: hasMore ? pageParam + 1 : null,
              };
            }}
            preloadQueryFn={async (id) => {
              if (!id) return null;
              const res = await shopSuppliersApi.list(shopId, {
                limit: 200,
                offset: 0,
                activeOnly: false,
              });
              return res.data.find((r) => r.id === id) ?? null;
            }}
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={supplierIdValue}
            onValueChange={(val) => {
              go({
                supplierId: val.trim() !== "" ? val : undefined,
              });
            }}
            placeholder="ຜູ້ສະໜອງ"
            className="h-8 w-full sm:w-56"
            clearable
          />
          <div className="flex w-full flex-col gap-1 sm:w-48">
            <span className="sr-only">ກຳນົດສົ່ງ</span>
            <SimpleSelect
              value={dueSelect}
              onValueChange={(v) => {
                go({
                  due: v === "__all__" ? undefined : (v as "soon" | "overdue"),
                });
              }}
              options={DUE_OPTIONS}
              placeholder="ກຳນົດສົ່ງ"
              className="h-8 w-full"
            />
          </div>
        </div>
        {hasFilter ? (
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() =>
              nav({
                to: PO_LIST_ROUTE,
                params: { shopId },
                search: {
                  offset: 0,
                  limit: search.limit ?? 20,
                },
              })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
